import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo, Brand } from "@/components/Logo";
import { ArrowLeft, Crown, ChevronRight, Infinity as InfinityIcon, AlignLeft, History, FileText, Image as ImageIcon, Zap, Camera } from "lucide-react";

const FEATURES = [
  { icon: InfinityIcon, title: "Unlimited AI chat responses", desc: "Ask as much as you want — no daily limit.", to: "/app" },
  { icon: AlignLeft, title: "Longer, more detailed responses", desc: "Deeper, more thorough answers than the free tier.", to: "/app" },
  { icon: History, title: "Unlimited saved chat history", desc: "Every chat saved and searchable.", to: "/app?history=1" },
  { icon: FileText, title: "Resume and cover letter builder", desc: "Upload, tailor to any job, download in Word or PDF.", to: "/resume" },
  { icon: ImageIcon, title: "AI Image generation chat responses", desc: "Generate images right inside chat.", to: "/app" },
  { icon: Camera, title: "Photo & document tools", desc: "Upload, identify, edit & enhance photos and docs.", to: "/photos" },
  { icon: Zap, title: "Priority new features", desc: "Get the newest aiBLACK tools first.", to: "/about" },
];

export default function ProPage() {
  const navigate = useNavigate();
  const { user, isPro } = useAuth();
  const pro = isPro || user?.role === "owner" || user?.role === "admin";

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="pro-page">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-[#2a3040]" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="pro-back" onClick={() => navigate("/app")} className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors"><ArrowLeft size={18} /> Back</button>
          <Logo size="text-xl" />
          <div className="w-12" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3"><Crown size={14} /> {pro ? "Pro active" : "aiBLACK Pro"}</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white mb-2">Your Pro features</h1>
        <p className="text-[#9ca3af] mb-8 max-w-xl">Tap any feature to jump straight to it.</p>

        {!pro && (
          <div className="bg-[#15151d] border border-[#FF5A00]/40 rounded-2xl p-6 mb-6 flex items-center justify-between gap-4" data-testid="pro-upsell">
            <p className="text-sm text-[#9ca3af]">These features are part of <Brand /> Pro.</p>
            <button data-testid="pro-upgrade" onClick={() => navigate("/app")} className="shrink-0 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-5 py-2.5 hover:bg-[#FF7A33] transition-colors">Upgrade</button>
          </div>
        )}

        <div className="space-y-3">
          {FEATURES.map((f, i) => (
            <button key={i} data-testid={`pro-feature-${i}`} onClick={() => navigate(f.to)}
              className="w-full group flex items-center gap-4 bg-[#15151d] border border-[#2a3040] hover:border-[#FF5A00] rounded-xl px-5 py-4 text-left transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#FF5A00]/15 flex items-center justify-center shrink-0">
                <f.icon size={18} className="text-[#FF5A00]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-white group-hover:text-[#FF5A00] transition-colors">{f.title}</div>
                <div className="text-sm text-[#9ca3af] truncate">{f.desc}</div>
              </div>
              <ChevronRight size={18} className="text-[#6b7280] group-hover:text-[#FF5A00] transition-colors shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
